import React, { useEffect, useMemo, createContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  getClusivitySeparatedPrices,
  mapVistaCartPrices,
  type Context,
} from '@vp/om-tax-display';
import pricingContextModuleHelpers from '@vp/pricing-context-module/helpers';
import { isVatInclusive } from '../../../apis/pricingContextApi';
import usePricingContext from '../../../hooks/usePricingContext';
import { pricingSelectors } from '../../../state/pricing';
import { cartItemSelectors } from '../../../state/cartItems';
import type {
  CustomClusivitySeparatedPrices,
  KeyedClusivitySeparatedPrices,
  TaxDisplayContextType,
} from '../../../types';

const TaxDisplayContext = createContext<TaxDisplayContextType>({
  displayTaxInclusivePricing: false,
  setDisplayTaxInclusivePricing: () => {},
  taxDisplayContext: {},
  clusivitySeparatedItemPrices: {},
  clusivitySeparatedItemGroupPrices: {},
  clusivitySeparatedShippingPrices: {},
  clusivitySeparatedTemplatePrices: {},
});

/**
 * 1. Responsible for determining whether tax inclusive or tax exclusive pricing should be shown.
 * The value can be toggled via debug tools or the vat toggle in the header if it is enabled.
 *
 * 2. Responsible for calculating "clusivity separated prices" for item, item template and shipping prices
 * using the `om-tax-display` library. It makes this data available elsewhere in the application to aggregate
 * and then display "tax aware prices" to customers.
 *
 * @param {string} tenant
 */
const TaxDisplayProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    pricingContextInitialized,
    displayTaxInclusivePricing,
    setDisplayTaxInclusivePricing,
  } = usePricingContext();

  const allItemIds = useSelector((state: any) =>
    cartItemSelectors.selectAllItemIds(state.cartItems)
  );
  const itemPricingData = useSelector((state: any) =>
    pricingSelectors.selectItemPricing(state.pricing)
  );
  const templatePricingSummaries = useSelector((state: any) =>
    pricingSelectors.selectTemplatePricingSummaries(state.pricing)
  );

  // determine if pricing context library should drive tax inclusivity
  useEffect(() => {
    if (pricingContextInitialized) {
      setDisplayTaxInclusivePricing(isVatInclusive());
    }
  }, [pricingContextInitialized, setDisplayTaxInclusivePricing]);

  const taxDisplayContext: Context = useMemo(
    () => ({
      taxDisplayInclusivityPreference: displayTaxInclusivePricing
        ? 'INCLUSIVE'
        : 'EXCLUSIVE',
    }),
    [displayTaxInclusivePricing]
  );

  useEffect(() => {
    /**
     * Custom event listener for VAT toggle changes.
     * See https://vistaprint.atlassian.net/wiki/spaces/PC/pages/20874846/Pricing+Context for details.
     */
    pricingContextModuleHelpers.executeAfterPricingContextEvent(
      pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
      (pricingContext: any) => {
        setDisplayTaxInclusivePricing(pricingContext.vatInclusive);
      },
      true
    );
  }, [setDisplayTaxInclusivePricing]);

  const taxDisplayContextValues = useMemo(() => {
    // map of {itemId: [clusivity separated price]}
    const clusivitySeparatedItemPrices: KeyedClusivitySeparatedPrices = {};
    // map of {itemGroupId: [clusivity separated price]}
    const clusivitySeparatedItemGroupPrices: KeyedClusivitySeparatedPrices = {};
    // map of {itemId: [clusivity separated price]}
    const clusivitySeparatedShippingPrices: KeyedClusivitySeparatedPrices = {};

    // clusivity separate item (and item group) prices
    if (allItemIds) {
      allItemIds.forEach((id: string) => {
        const thisItemsPricingData = itemPricingData[id];
        if (thisItemsPricingData) {
          // first, get the "clusivity separate item prices" for the item and insert it into the item map
          const { components = [], taxes = [], groupId } = thisItemsPricingData;
          const mappedPrices = mapVistaCartPrices(
            components.map((comp: any) => ({
              ...comp,
              // tagging the item pricing components with 'product' and their name before mapping
              identifiers: ['product', comp.name],
            })),
            taxes
          );
          const clusivitySeparatedPrices: CustomClusivitySeparatedPrices[] =
            mappedPrices.map(price =>
              getClusivitySeparatedPrices(price.components, price.taxes)
            );

          clusivitySeparatedItemPrices[id] = clusivitySeparatedPrices;

          // next if the item is in a group, insert it into the group map
          if (groupId) {
            const currentGroupPrices: CustomClusivitySeparatedPrices[] =
              clusivitySeparatedItemGroupPrices[groupId] || [];
            currentGroupPrices.push(...clusivitySeparatedPrices);
            clusivitySeparatedItemGroupPrices[groupId] = currentGroupPrices;
          }

          // shipping
          if (thisItemsPricingData.shippingPricingData) {
            const {
              components: shippingComponents = [],
              taxes: shippingTaxes = [],
            } = thisItemsPricingData.shippingPricingData;

            const mappedShippingPrices = mapVistaCartPrices(
              shippingComponents,
              shippingTaxes
            );

            clusivitySeparatedShippingPrices[id] = mappedShippingPrices.map(
              price =>
                getClusivitySeparatedPrices(price.components, price.taxes)
            );
          }
        }
      });
    }

    // clusivity separated template prices
    // map of {itemId: [clusivity separated price]}
    const clusivitySeparatedTemplatePrices: KeyedClusivitySeparatedPrices = {};
    Object.keys(templatePricingSummaries).forEach(id => {
      const summary = templatePricingSummaries[id];
      if (summary) {
        const { components = [], taxes = [] } = summary;
        const mappedPrices = mapVistaCartPrices(
          components.map((component: any) => ({
            ...component,
            // tagging the template pricing components with their name before mapping
            identifiers: ['product', component.name],
          })),
          taxes
        );
        clusivitySeparatedTemplatePrices[id] = mappedPrices.map(price =>
          getClusivitySeparatedPrices(price.components, price.taxes)
        );
      }
    });

    return {
      displayTaxInclusivePricing,
      setDisplayTaxInclusivePricing,
      taxDisplayContext,
      clusivitySeparatedItemPrices,
      clusivitySeparatedItemGroupPrices,
      clusivitySeparatedShippingPrices,
      clusivitySeparatedTemplatePrices,
    };
  }, [
    allItemIds,
    templatePricingSummaries,
    displayTaxInclusivePricing,
    setDisplayTaxInclusivePricing,
    taxDisplayContext,
    itemPricingData,
  ]);

  return (
    <TaxDisplayContext.Provider value={taxDisplayContextValues}>
      {children}
    </TaxDisplayContext.Provider>
  );
};

TaxDisplayProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export { TaxDisplayContext, TaxDisplayProvider };
